// Supplier create / edit / show dialog for the Crew & Network page. Fully
// controlled — the parent owns the `supForm` state ({ mode, readOnly, data }),
// the save handler and the busy flag.
//
// For ONLINE suppliers (linked to a marketplace/user account) the company
// maintains its own details on the platform, so the form opens READ-ONLY:
// company fields + categories are display-only; only internal Notes and file
// uploads are editable. Field names match the marketplace "Company details".

import { useEffect, useMemo, useState } from "react";
import { Country, City } from "country-state-city";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import AttachmentsPanel from "../../../components/AttachmentsPanel";
import { UNIFIED_SYSTEMS } from "../../../lib/systems";
import { ComboBox } from "../../../components/ComboBox";
import { SupBizField } from "./helpers";
import { api } from "../../../lib/api";

export function SupplierFormDialog({ supForm, setSupForm, submitSupplier, supBusy, activeTour }) {
  const upd = (patch) => setSupForm({ ...supForm, data: { ...supForm.data, ...patch } });
  const data = supForm?.data;
  const readOnly = !!supForm?.readOnly;

  // Duplicate check — warn when the email already exists in the shared
  // supplier database so the yacht links instead of duplicating.
  const [dup, setDup] = useState(null);
  const email = data?.email || "";
  useEffect(() => {
    if (readOnly || supForm?.mode === "edit") { setDup(null); return; }
    const e = email.trim().toLowerCase();
    if (!e.includes("@") || e.length < 6) { setDup(null); return; }
    const t = setTimeout(() => {
      api.get("/suppliers/check-email", { params: { email: e } })
        .then(({ data: res }) => setDup(res?.exists ? res : null))
        .catch(() => setDup(null));
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, readOnly, supForm?.mode]);

  const countryNames = useMemo(() => Country.getAllCountries().map((c) => c.name), []);
  const nameToIso = useMemo(() => Object.fromEntries(Country.getAllCountries().map((c) => [c.name, c.isoCode])), []);
  const cityNames = useMemo(() => {
    const iso = nameToIso[data?.country];
    if (!iso) return [];
    return [...new Set((City.getCitiesOfCountry(iso) || []).map((c) => c.name))];
  }, [data?.country, nameToIso]);

  const toggleCat = (name) => {
    const cur = data.categories || [];
    upd({ categories: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] });
  };

  const selectedCats = (data?.categories && data.categories.length ? data.categories : [data?.category]).filter(Boolean);

  return (
    <Dialog open={!!supForm} onOpenChange={(o) => { if (!o) setSupForm(null); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="supplier-modal"
        onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
        onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{readOnly ? "Supplier details" : (supForm?.mode === "edit" ? "Edit supplier" : "Add supplier")}</DialogTitle>
          <DialogDescription>
            {readOnly
              ? "This supplier maintains their own company profile on Seavanto, so these fields are read-only. You can still add internal notes and upload files."
              : "Email and phone are required. No invite is sent — use the dedicated Send invite button later."}
          </DialogDescription>
        </DialogHeader>
        {supForm && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">Company name *</label>
              <Input value={data.name} onChange={(e) => upd({ name: e.target.value })} disabled={readOnly} readOnly={readOnly} data-testid="supplier-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Contact person (optional)</label>
              <Input value={data.contact_person} onChange={(e) => upd({ contact_person: e.target.value })} placeholder="Jane Smith" disabled={readOnly} readOnly={readOnly} data-testid="supplier-contact-person" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-zinc-600">Email *</label>
                <Input type="email" value={data.email} onChange={(e) => upd({ email: e.target.value })} disabled={readOnly} readOnly={readOnly} data-testid="supplier-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600">Phone *</label>
                <Input value={data.phone} onChange={(e) => upd({ phone: e.target.value })} placeholder="+1 555 0000" disabled={readOnly} readOnly={readOnly} data-testid="supplier-phone" />
              </div>
            </div>
            {dup && !readOnly && supForm?.mode !== "edit" && (
              <div
                className={`rounded-md border px-3 py-2.5 text-xs leading-relaxed ${dup.in_my_network ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
                data-testid="supplier-duplicate-warning"
              >
                {dup.in_my_network ? (
                  <>Already in your network as <strong>{dup.card_name || dup.company_name}</strong> ({dup.supplier_no}). Saving reuses that existing card — no duplicate is created.</>
                ) : (
                  <><strong>{dup.company_name}</strong> ({dup.supplier_no} · {dup.supplier_mode}) already exists in the Seavanto supplier database. Saving links your yacht to that profile instead of creating a duplicate.</>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-zinc-600">Categories</label>
              {readOnly ? (
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="supplier-categories">
                  {selectedCats.length ? selectedCats.map((c) => (
                    <span key={c} className="text-xs px-2 py-1 rounded-full border border-zinc-200 text-zinc-600">{c}</span>
                  )) : <span className="text-xs text-zinc-400">No categories set by the supplier.</span>}
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1" data-testid="supplier-categories">
                  {UNIFIED_SYSTEMS.map((name) => (
                    <button key={name} type="button" onClick={() => toggleCat(name)}
                      className={`text-xs px-2 py-1 rounded-full border ${(data.categories || []).includes(name) ? "bg-ink text-white border-ink" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                      data-testid={`supplier-cat-${name}`}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Notes <span className="text-zinc-400">(internal)</span></label>
              <textarea
                className="mt-1 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm min-h-[70px]"
                value={data.notes}
                onChange={(e) => upd({ notes: e.target.value })}
                placeholder="Private notes for your team — never shown to the supplier."
                data-testid="supplier-notes"
              />
            </div>

            <details
              className="rounded-md border border-zinc-200 bg-zinc-50/40 open:bg-white"
              data-testid="supplier-business-details"
              open
            >
              <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-900">
                Business details
              </summary>
              <div className="space-y-3 px-3 pb-3 pt-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SupBizField label="Website" testid="supplier-website" value={data.website} onChange={(v) => upd({ website: v })} placeholder="https://" disabled={readOnly} />
                  <SupBizField label="VAT number" testid="supplier-vat-number" value={data.vat_number} onChange={(v) => upd({ vat_number: v })} disabled={readOnly} />
                  <SupBizField label="Tax ID number" testid="supplier-tax-id" value={data.tax_id} onChange={(v) => upd({ tax_id: v })} disabled={readOnly} />
                </div>

                <div className="text-[11px] uppercase tracking-wider text-zinc-500 pt-2">Address</div>
                <SupBizField label="Address" testid="supplier-address" value={data.address} onChange={(v) => upd({ address: v })} placeholder="Street / marina, building, unit" disabled={readOnly} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-zinc-600">Country</label>
                    <div className="mt-1">
                      {readOnly
                        ? <Input value={data.country || ""} disabled readOnly data-testid="supplier-country" />
                        : <ComboBox value={data.country} onChange={(v) => upd({ country: v, city: "" })} options={countryNames} placeholder="Select country" testid="supplier-country" />}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-zinc-600">City</label>
                    <div className="mt-1">
                      {readOnly
                        ? <Input value={data.city || ""} disabled readOnly data-testid="supplier-city" />
                        : <ComboBox value={data.city} onChange={(v) => upd({ city: v })} options={cityNames} placeholder={data.country ? "Select or add city" : "Type a city"} testid="supplier-city" />}
                    </div>
                  </div>
                  <SupBizField label="Postcode" testid="supplier-postcode" value={data.postcode} onChange={(v) => upd({ postcode: v })} disabled={readOnly} />
                </div>

                <div className="text-[11px] uppercase tracking-wider text-zinc-500 pt-2">Bank</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SupBizField label="Bank name" testid="supplier-bank-name" value={data.bank_name} onChange={(v) => upd({ bank_name: v })} disabled={readOnly} />
                  <SupBizField label="Account holder" testid="supplier-bank-holder" value={data.bank_account_holder} onChange={(v) => upd({ bank_account_holder: v })} disabled={readOnly} />
                  <SupBizField label="Account number" testid="supplier-bank-account" value={data.bank_account_number} onChange={(v) => upd({ bank_account_number: v })} disabled={readOnly} />
                  <SupBizField label="IBAN" testid="supplier-bank-iban" value={data.bank_iban} onChange={(v) => upd({ bank_iban: v })} disabled={readOnly} />
                  <SupBizField label="BIC / SWIFT" testid="supplier-bank-bic" value={data.bank_bic_swift} onChange={(v) => upd({ bank_bic_swift: v })} disabled={readOnly} />
                </div>
              </div>
            </details>

            {supForm.mode === "edit" && data.id && (
              <div className="rounded-md border border-zinc-200 bg-zinc-50/40 p-3" data-testid="supplier-files-editable">
                <AttachmentsPanel kind="supplier" refId={data.id} editable title="Files (PDFs & photos)" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSupForm(null)} data-testid="supplier-cancel">Cancel</Button>
          <Button onClick={submitSupplier} disabled={supBusy} className="bg-ink hover:bg-black" data-testid="supplier-save">
            {supBusy ? "Saving…" : (readOnly ? "Save notes" : (supForm?.mode === "edit" ? "Save" : "Add supplier"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
